import Joyride, { CallBackProps, EVENTS } from "react-joyride";
import WalkThroughToolTip from "./WalkThroughTooltip/WalkThroughToolTip";
import { WALKTHROUGH_STEP_KEY } from "@/app/_constants/walkthrough";
import useDeviceType from "@/app/_hooks/useDeviceType";
import {
  useComputedColorScheme,
  useMantineTheme,
  Text,
  Flex,
  Anchor,
  Stack,
  List,
  Group,
  Kbd,
} from "@mantine/core";
import { usePathname, useRouter } from "next/navigation";
import { Component, ReactNode, useEffect, useRef, useState } from "react";
import {
  reportBoundaryError,
  BoundaryError,
} from "@/app/_helpers/errorReporting";
import { useUserContext } from "@/app/_hooks/useUserContext";
import { useSubscriptionContext } from "@/app/_hooks/subscription/useSubscriptionContext";
import {
  IconBrandInstagram,
  IconBrandFacebook,
  IconUsers,
  IconMail,
} from "@tabler/icons-react";

// react-joyride/popper can throw internally (e.g. measuring a tour target
// that unmounted during navigation). A crashed tour must never take down the
// whole app for a brand-new user — catch it, report it, and end the tour.
class WalkThroughErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: BoundaryError) {
    if (process.env.NODE_ENV === "production") {
      reportBoundaryError(error, "walkthrough (tour disabled after crash)");
    }
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

// The page each step's target lives on, so a resumed tour can navigate back
// to the right place before re-anchoring. Steps not listed anchor on /jobs.
const stepPaths: Record<string, string> = {
  "Clients Board": "/clients",
  "Client Board Features": "/clients",
  "New Job": "/newjob",
  "New Client": "/newclient",
  Inventory: "/inventory",
  Expenses: "/expenses",
  Reports: "/reports",
  Settings: "/settings",
  "Dark & Light Mode": "/settings",
  Support: "/settings",
  "Log out": "/settings",
};

const WalkThrough = () => {
  const user = useUserContext();
  const subscription = useSubscriptionContext();
  // Inventory + Expenses are a Standard+ feature, so their tour steps (and
  // their nav targets) don't exist on Lite — skip them in the walkthrough.
  const isLite = subscription.level === "lite";
  const router = useRouter();
  const { isMobile } = useDeviceType();
  const computedColorScheme = useComputedColorScheme();
  const theme = useMantineTheme();
  const [stepIndex, setStepIndex] = useState(0);
  // Bumped to remount Joyride when a step's target appears after a route
  // change, so it re-evaluates a target it previously reported missing.
  const [joyrideKey, setJoyrideKey] = useState(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingIndexRef = useRef<number | null>(null);

  const backStep = () => setStepIndex(stepIndex - 1);
  const nextStep = () => setStepIndex(stepIndex + 1);
  const pathname = usePathname();

  const steps = isMobile
    ? [
        {
          target: ".Jobs-Walkthrough",
          title: "Welcome to MountMonitor!",
          content: (
            <Stack gap="xs">
              <Text size="md">Welcome to MountMonitor!</Text>
              <Text size="md">
                We&apos;ve detected you&apos;re on a mobile device. While
                MountMonitor works on mobile, the guided tour is best
                experienced on a tablet or computer.
              </Text>
              <Text size="md" c="dimmed">
                Don&apos;t worry - you&apos;ll be prompted for the full
                walkthrough when you log in from a larger device.
              </Text>
            </Stack>
          ),
          placement: "center",
          offset: -10,
          data: {
            next: () => {
              nextStep();
            },
          },
        },
      ]
    : [
        {
          target: ".Jobs-Walkthrough",
          title: "Welcome to MountMonitor",
          content: (
            <Stack gap="xs" mt="sm">
              <Text size="md">
                Let&apos;s get your shop up and running quickly and set up for
                success!
              </Text>

              <Text size="md">
                This quick tour will help you navigate the platform and provide
                an introduction to your new digital workbench — everything you
                need to manage your taxidermy business in one place.
              </Text>

              <Text size="md" c="dimmed">
                Note: we&apos;ve added a sample client and job to get you
                started.
              </Text>
            </Stack>
          ),
          placement: "center",
          offset: -10,
          data: {
            next: () => {
              nextStep();
            },
          },
        },
        // 0
        {
          target: ".Jobs-Walkthrough",
          title: "Jobs Board",
          content: (
            <Stack gap="xs">
              <Text size="md">The jobs board is your central command hub.</Text>
              <Text size="md">
                See all your projects at a glance – Track progress, manage
                deadlines, and keep everything organized in one powerful view.
              </Text>
            </Stack>
          ),
          placement: "auto",
          offset: -10,
          data: {
            back: () => {
              backStep();
            },
            next: () => {
              nextStep();
            },
          },
        },
        {
          target: ".Search-WalkThrough",
          title: "Search Jobs",
          content: (
            <Stack gap="xs">
              <Text size="md">
                Lightning-fast search to find exactly what you need.
              </Text>
              <Text size="md">
                Type a job number or client&apos;s last name - results appear
                instantly.
              </Text>
            </Stack>
          ),
          placement: "auto",
          offset: -10,
          data: {
            back: () => {
              backStep();
            },
            next: () => {
              nextStep();
            },
          },
        },
        // 2
        {
          target: ".Filters-WalkThrough",
          title: "Filter Jobs",
          content: (
            <Stack gap="xs">
              <Text size="md">
                Find exactly what you need with comprehensive job filtering:
              </Text>
              <List size="sm" spacing="xs" maw={300}>
                <List.Item>Filter by species, mount type, or service</List.Item>
                <List.Item>
                  Use job tags or mount & service tags to find groups of jobs
                </List.Item>
                <List.Item>
                  Filter by workflow stages, payment status, or priority levels
                </List.Item>
                <List.Item>
                  Narrow by created date, estimated completion, or payment
                  received dates
                </List.Item>
              </List>
              <Text size="md">
                Combine multiple filters to create precise views of your
                workflow.
              </Text>
            </Stack>
          ),
          placement: "auto",
          offset: -10,
          data: {
            back: () => {
              backStep();
            },
            next: () => {
              nextStep();
            },
          },
        },
        // 3
        {
          target: ".Boards-WalkThrough",
          title: "Boards",
          content: (
            <Stack gap="xs">
              <Text size="md">
                Boards are your personal workflow shortcuts.
              </Text>
              <Text size="md">
                Save filter combinations you use daily as a &quot;board&quot;
                and access them with one click.
              </Text>
              <Text size="md">Here are some examples:</Text>

              <List size="sm" spacing="xs" maw={300}>
                <List.Item>
                  <strong>This Week&apos;s Rush Jobs</strong> - high priority,
                  due soon
                </List.Item>
                <List.Item>
                  <strong>Awaiting Deposit</strong> - awaiting deposit from
                  client
                </List.Item>
                <List.Item>
                  <strong>Bear Rugs - Finishing</strong> - all bears which are
                  rugs in the finishing stage
                </List.Item>
                <List.Item>
                  <strong>Deer Shoulders - Mounting</strong> - all deer
                  shoulders in the mounting stage
                </List.Item>
              </List>
              <Text size="md">
                Create unlimited boards for any workflow, never waste time
                recreating the same filters again.
              </Text>
            </Stack>
          ),
          placement: "auto",
          offset: -10,
          data: {
            back: () => {
              backStep();
            },
            next: () => {
              nextStep();
            },
          },
        },
        // 4
        {
          target: ".LoadBoard-WalkThrough",
          title: "Load Board",
          content: (
            <Stack gap="xs">
              <Text size="md">One click to load your saved board.</Text>
              <Text size="md">
                Boards remain active as you navigate around MountMonitor. No
                need to set them every time you come to the jobs board.
              </Text>
            </Stack>
          ),
          placement: "auto",
          offset: -10,
          data: {
            back: () => {
              backStep();
            },
            next: () => {
              nextStep();
            },
          },
        },
        // 5
        {
          target: ".JobNumLink-WalkThrough",
          title: "View Job",
          content: (
            <Stack gap="xs">
              <Text size="md">Every job detail, one click away.</Text>
              <Text size="md">Click any job number to:</Text>
              <List size="sm" spacing="xs">
                <List.Item>Track every mount and all its details</List.Item>
                <List.Item>Manage billing, payments, and invoices</List.Item>
                <List.Item>Store reference photos and documents</List.Item>
                <List.Item>
                  Job history – see who changed what and when
                </List.Item>
                <List.Item>Print job cards for your shop floor</List.Item>
                {subscription.level === "pro" ||
                subscription.level === "master" ? (
                  <List.Item>Generate mount tags</List.Item>
                ) : null}
              </List>
            </Stack>
          ),
          placement: "auto",
          offset: -10,
          data: {
            back: () => {
              backStep();
            },
            next: () => {
              nextStep();
            },
          },
        },
        // 6
        {
          target: ".ClientLink-WalkThrough",
          title: "View Linked Client",
          content: (
            <Stack gap="xs">
              <Text size="md">Know your clients inside and out.</Text>
              <Text size="md">One click reveals everything:</Text>
              <List size="sm" spacing="xs">
                <List.Item>
                  Contact details and communication preferences
                </List.Item>
                <List.Item>Complete job history at a glance</List.Item>
                <List.Item>Current balance and payment patterns</List.Item>
                <List.Item>Reference photos and important documents</List.Item>
                <List.Item>Full audit trail of all interactions</List.Item>
              </List>
            </Stack>
          ),
          placement: "auto",
          offset: -10,
          data: {
            back: () => {
              backStep();
            },
            next: () => {
              nextStep();
            },
          },
        },
        {
          target: ".JobActions-WalkThrough",
          title: "Job Quick Actions",
          content: (
            <Stack gap="xs">
              <Text size="md">Quick actions - right where you need them.</Text>
              <Text size="md">
                Edit, print, and manage without leaving the jobs board. Common
                tasks are just one click away.
              </Text>
            </Stack>
          ),
          placement: "auto",
          offset: -10,
          data: {
            back: () => {
              backStep();
            },
            next: () => {
              router.push("/clients");

              nextStep();
            },
          },
        },

        {
          target: ".Clients-Walkthrough",
          title: "Clients Board",
          content: (
            <Stack gap="xs">
              <Text size="md">
                Your Clients board- where relationships are managed.
              </Text>
              <Text size="md">
                Know your clients inside and out. View your complete work
                history together in seconds. Browse, filter, and access
                everything about each client.
              </Text>
            </Stack>
          ),
          placement: "auto",
          offset: -10,
          data: {
            back: () => {
              router.push("/jobs");

              backStep();
            },
            next: () => {
              nextStep();
            },
          },
        },
        {
          target: ".Clients-Walkthrough",
          title: "Client Board Features",
          content: (
            <Stack gap="xs">
              <Text size="md">
                All the same powerful tools as the jobs board.
              </Text>
              <Text size="md">
                Search, filter, and create boards for clients too. Find frequent
                customers, track big spenders, or see who needs a follow-up.
              </Text>
            </Stack>
          ),
          placement: "center",
          offset: -10,
          data: {
            back: () => {
              backStep();
            },
            next: () => {
              router.push("/newjob");
              nextStep();
            },
          },
        },
        {
          target: ".New-Job-Walkthrough",
          title: "New Job",
          content: (
            <Stack gap="xs">
              <Text size="md">Create a job from anywhere.</Text>
              <Text size="md">
                Click <strong>New Job</strong> — a purpose-built form guides you
                through each step, so you capture everything from the start.
              </Text>

              <Text size="md" lh={1.2} mt="sm">
                💡 Pro tip: All form fields in MountMonitor are navigable with
                your keyboard.
                <br />
                <br />
                <Kbd>Tab</Kbd> to move forward
                <br />
                <br />
                <Kbd>Shift</Kbd> + <Kbd>Tab</Kbd> to move back.
                <br />
                <br />
                <Kbd>Arrow up</Kbd> and <Kbd>Arrow down</Kbd> to scroll lists.
                <br />
                <br />
                <Kbd>Enter</Kbd> to select a value from a list.
              </Text>

              <Text size="md" c="dimmed" mt="sm">
                Note: Don&apos;t ever record a certain detail? Let us know — we
                can hide fields for you.
              </Text>
            </Stack>
          ),
          placement: "right",
          offset: -10,
          data: {
            back: () => {
              router.push("/clients");
              backStep();
            },
            next: () => {
              router.push("/newclient");
              nextStep();
            },
          },
        },

        {
          target: ".New-Client-Walkthrough",
          title: "New Client",
          content: (
            <Stack gap="xs">
              <Text size="md">Add a client in seconds.</Text>
              <Text size="md">
                Click <strong>New Client</strong> — contact details,
                preferences, communication settings, and notes, all in one
                place.
              </Text>
            </Stack>
          ),
          placement: "right",
          offset: -10,
          data: {
            back: () => {
              router.push("/newjob");
              backStep();
            },
            next: () => {
              router.push(isLite ? "/reports" : "/inventory");
              nextStep();
            },
          },
        },

        {
          target: ".Inventory-WalkThrough",
          title: "Inventory",
          content: (
            <Stack gap="xs">
              <Text size="md">Track your supplies and where they go.</Text>
              <List size="sm" spacing="xs" maw={360}>
                <List.Item>
                  <strong>Stock:</strong> Live counts with low-stock alerts.
                </List.Item>
                <List.Item>
                  <strong>Orders:</strong> Raise purchase orders and receive
                  stock as it arrives.
                </List.Item>
                <List.Item>
                  <strong>Supplier Catalogs:</strong> Browse your
                  suppliers&apos; products and add them straight to inventory.
                </List.Item>
              </List>
              <Text size="md">
                Assign materials to a job and stock is deducted automatically —
                feeding the true cost of each job.
              </Text>
            </Stack>
          ),
          placement: "auto",
          offset: -10,
          data: {
            back: () => {
              router.push("/newclient");
              backStep();
            },
            next: () => {
              router.push("/expenses");
              nextStep();
            },
          },
        },

        {
          target: ".Expenses-WalkThrough",
          title: "Expenses",
          content: (
            <Stack gap="xs">
              <Text size="md">
                See what your work costs — and what you spend.
              </Text>
              <List size="sm" spacing="xs" maw={360}>
                <List.Item>
                  <strong>Used on Jobs:</strong> Material cost per job and
                  whether each one made money.
                </List.Item>
                <List.Item>
                  <strong>Money Spent:</strong> Real cash out — received orders
                  plus one-off buys like rent or fuel, with receipts attached.
                </List.Item>
              </List>
            </Stack>
          ),
          placement: "auto",
          offset: -10,
          data: {
            back: () => {
              router.push("/inventory");
              backStep();
            },
            next: () => {
              router.push("/reports");
              nextStep();
            },
          },
        },

        {
          target: ".CSVreports-WalkThrough",
          title: "Reports",
          content: (
            <Stack gap="xs">
              <Text size="md">
                Gain insights into your business and operations.
              </Text>
              <Text size="md">
                Financial reports — Profit &amp; Loss, Cash Flow, Job
                Profitability and Payments — plus operational reports, all
                filterable and exportable to CSV.
              </Text>
            </Stack>
          ),
          placement: "auto",
          offset: -10,
          data: {
            back: () => {
              router.push(isLite ? "/newclient" : "/expenses");
              backStep();
            },
            next: () => {
              router.push("/settings");
              nextStep();
            },
          },
        },

        {
          target: ".Settings",
          title: "Settings",
          content: (
            <Stack gap="xs">
              <Text size="md">Make MountMonitor yours.</Text>
              <Text size="md">
                Every shop is unique - customize everything:
              </Text>
              <List size="sm" spacing="xs" maw={550}>
                <List.Item>
                  <strong>General:</strong> Core business details, logo, store
                  name, contact numbers, address, timezone, currency and
                  measurement system
                </List.Item>
                <List.Item>
                  <strong>Client Portal:</strong> Automated SMS & email
                  configuration, brand appearance, stage images and FAQs for
                  client self-service
                </List.Item>
                <List.Item>
                  <strong>Price List:</strong> Set your price list once -
                  auto-populates pricing fields when used in jobs
                </List.Item>
                <List.Item>
                  <strong>Invoicing:</strong> Tax rates, processing fees,
                  invoice customization and QuickBooks syncing
                </List.Item>
                <List.Item>
                  <strong>Contracts:</strong> Build contract templates, send
                  them for e-signature, and keep every signed agreement on file
                </List.Item>
                <List.Item>
                  <strong>Job Templates:</strong> Create templates for common
                  jobs (e.g. Whitetail Deer Full Sneak Shoulder Mount) for
                  one-click setup
                </List.Item>
                <List.Item>
                  <strong>Mount Types:</strong> All the kinds of mounts you do
                  with custom measurement fields
                </List.Item>
                <List.Item>
                  <strong>Stages:</strong> Your workflow stages - names, colors,
                  and order of progression
                </List.Item>
                <List.Item>
                  <strong>Locations:</strong> Map out your facility to assign
                  capes, skulls, antlers and any other parts to individual
                  locations - fully integrated with our Track and Trace system
                </List.Item>
                {subscription.level !== "lite" && (
                  <List.Item>
                    <strong>Team:</strong> Manage team roles and permissions
                    affecting what they can see and do when logged in
                  </List.Item>
                )}
                {subscription.level === "pro" ||
                subscription.level === "master" ? (
                  <List.Item>
                    <strong>Printer:</strong> Manage your Track and Trace
                    printer for generating mount tags
                  </List.Item>
                ) : null}
                <List.Item>
                  <strong>Referrals:</strong> Refer other shops and earn rewards
                  when they come on board
                </List.Item>
              </List>
              <Text size="md" mt="sm">
                Set up once, save hours every week.
              </Text>
            </Stack>
          ),

          placement: "center",

          data: {
            back: () => {
              router.push("/clients");
              backStep();
            },
            next: () => {
              nextStep();
            },
          },
        },

        {
          target: ".Dark-WalkThrough",
          title: "Dark & Light Mode",
          content: (
            <Text size="md">
              Switch themes to match your preference or lighting.
            </Text>
          ),
          placement: "auto",
          offset: -10,
          data: {
            back: () => {
              backStep();
            },
            next: () => {
              nextStep();
            },
          },
        },
        {
          target: ".Report-WalkThrough",
          title: "Support",
          content: (
            <Stack gap="sm">
              <Text size="md">We&apos;re here when you need us.</Text>
              <Text size="md">
                Found a bug? Have an idea? Stuck on something? Open a support
                ticket here. You can see every ticket you have raised, its
                status, and our replies, all inside MountMonitor.
              </Text>
              <Text size="md">
                💡 Pro tip: Tickets raised here are prioritized and receive the
                fastest responses.
              </Text>
            </Stack>
          ),

          placement: "auto",
          offset: -10,
          data: {
            back: () => {
              backStep();
            },
            next: () => {
              nextStep();
            },
          },
        },
        {
          target: ".Logout-WalkThrough",
          title: "Log out",
          content: (
            <Text size="md">
              Sign out when you&apos;re done. Your data is always secure.
            </Text>
          ),
          placement: "auto",
          offset: -10,
          data: {
            back: () => {
              backStep();
            },
            next: () => {
              router.push("/jobs");
              nextStep();
            },
          },
        },
        {
          target: ".Jobs",
          title: "Community",
          content: (
            <Stack gap="sm">
              <Text size="md">
                You&apos;re joining a community of successful taxidermists.
              </Text>
              <Text size="md">
                Follow us and connect with other MountMonitor users who are
                serious about growing their businesses.
              </Text>

              <Stack gap="xs">
                <Group gap="xs">
                  <IconUsers size={18} />
                  <Anchor
                    size="sm"
                    href="https://www.facebook.com/share/g/19ZjZ62XoG/"
                    target="_blank"
                  >
                    Private Facebook Group
                  </Anchor>
                </Group>
                <Group gap="xs">
                  <IconBrandInstagram size={18} />
                  <Anchor
                    size="sm"
                    href="https://instagram.com/mountmonitor_"
                    target="_blank"
                  >
                    Instagram
                  </Anchor>
                </Group>
                <Group gap="xs">
                  <IconBrandFacebook size={18} />
                  <Anchor
                    size="sm"
                    href="https://www.facebook.com/mountmonitorapp/"
                    target="_blank"
                  >
                    Facebook Page
                  </Anchor>
                </Group>
                <Group gap="xs">
                  <IconMail size={18} />
                  <Anchor size="sm" href="mailto:contact@mountmonitor.com">
                    contact@mountmonitor.com
                  </Anchor>
                </Group>
              </Stack>

              <Text size="md" c="dimmed">
                The Private Facebook Group is a great place to share tips,
                celebrate successes, and get early access to MountMonitor news
                and updates.
              </Text>
            </Stack>
          ),

          placement: "center",
          offset: -10,
          data: {
            back: () => {
              router.push("/settings");
              backStep();
            },
            next: () => {
              nextStep();
            },
          },
        },
        {
          target: ".Jobs",
          title: "One more thing",
          content: (
            <Stack gap="xs">
              <Text size="md">
                You&apos;ve got notifications waiting — check the bell icon in
                the top left of the navigation bar.
              </Text>

              <Text size="md" fw={500}>
                Let&apos;s unlock your shop&apos;s full potential!
              </Text>
            </Stack>
          ),

          placement: "center",
          offset: -10,
          data: {
            back: () => {
              backStep();
            },
            next: () => {
              nextStep();
            },
          },
        },
      ];

  // Drop the Standard+ Inventory/Expenses steps for Lite users (their nav
  // targets don't render, which would otherwise break the tour).
  const visibleSteps = isLite
    ? steps.filter((s) => s.title !== "Inventory" && s.title !== "Expenses")
    : steps;

  // Resume a part-done tour where it left off instead of restarting at step 0
  // on every login, and land on the page that step anchors to.
  useEffect(() => {
    if (user.walkThroughCompleted !== false) return;
    let saved = 0;
    try {
      saved =
        parseInt(localStorage.getItem(WALKTHROUGH_STEP_KEY) ?? "0", 10) || 0;
    } catch {}
    const resumeIndex = Math.min(Math.max(saved, 0), visibleSteps.length - 1);
    if (resumeIndex > 0) {
      setStepIndex(resumeIndex);
    }
    const path = stepPaths[visibleSteps[resumeIndex]?.title] ?? "/jobs";
    if (pathname !== path) {
      router.push(path);
    }
  }, []);

  useEffect(() => {
    try {
      if (stepIndex > 0) {
        localStorage.setItem(WALKTHROUGH_STEP_KEY, String(stepIndex));
      }
    } catch {}
  }, [stepIndex]);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  // In controlled mode Joyride does NOT advance past a missing target: it
  // fires TARGET_NOT_FOUND and waits forever, which used to strand the tour
  // (and the completion flag) with no visible error. Poll briefly in case the
  // target is still mounting after a route change; if it shows up, remount
  // Joyride to re-anchor, otherwise skip the step like a Next click.
  const handleJoyrideCallback = (data: CallBackProps) => {
    if (data.type !== EVENTS.TARGET_NOT_FOUND) return;
    if (pollingIndexRef.current === data.index) return;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollingIndexRef.current = data.index;

    const step = visibleSteps[data.index];
    const startedAt = Date.now();
    const poll = () => {
      if (
        typeof step?.target === "string" &&
        document.querySelector(step.target)
      ) {
        pollingIndexRef.current = null;
        setJoyrideKey((k) => k + 1);
        return;
      }
      if (Date.now() - startedAt > 5000 || !step) {
        pollingIndexRef.current = null;
        step?.data?.next?.();
        return;
      }
      pollTimerRef.current = setTimeout(poll, 250);
    };
    poll();
  };

  return (
    <WalkThroughErrorBoundary>
      <Joyride
        key={joyrideKey}
        run={user.walkThroughCompleted === false}
        // @ts-ignore
        steps={visibleSteps}
        callback={handleJoyrideCallback}
        disableCloseOnEsc
        disableOverlayClose
        continuous
        disableScrolling
        disableScrollParentFix
        tooltipComponent={WalkThroughToolTip}
        stepIndex={stepIndex}
        styles={{
          options: {
            width: 500,
            arrowColor:
              computedColorScheme === "light"
                ? theme.white
                : theme.colors.dark[7],
            zIndex: 10000000,
          },
        }}
      />
    </WalkThroughErrorBoundary>
  );
};

export default WalkThrough;
